import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/Toast';

const NodeEditor = ({ storyId, nodeId, onSave, onCancel }) => {
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [options, setOptions] = useState([]);
  const [availableNodes, setAvailableNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isStartNode, setIsStartNode] = useState(false);
  const [isEndNode, setIsEndNode] = useState(false);
  const [previousNode, setPreviousNode] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [showExistingImages, setShowExistingImages] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showPreviousNode, setShowPreviousNode] = useState(false);
  const [quickviewNode, setQuickviewNode] = useState(null);
  const [quickviewData, setQuickviewData] = useState(null);
  const [loadingQuickview, setLoadingQuickview] = useState(false);
  const [savedNodeId, setSavedNodeId] = useState(null); // Track if we've already saved a node
  const quickviewRef = useRef(null);
  const navigate = useNavigate();

  // Load node data if editing an existing node
  const loadNodeData = async (id) => {
    if (!id || id === 'new') return setLoading(false);

    try {
      setLoading(true);
      // Fetch node data
      const { data: nodeData, error: nodeError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('*')
        .eq('id', id)
        .single();

      if (nodeError) throw nodeError;

      if (nodeData) {
        setContent(nodeData.content || '');
        setImageUrl(nodeData.image_url || '');
        setIsStartNode(nodeData.is_start_node || false);
        setIsEndNode(nodeData.is_end_node || false);
        if (nodeData.image_url) {
          setImagePreview(nodeData.image_url);
        }
      }

      // Fetch options for this node
      const { data: optionsData, error: optionsError } = await supabase
        .from('options_sb2847x9')
        .select('*')
        .eq('story_node_id', id);

      if (optionsError) throw optionsError;
      setOptions(optionsData || []);

      // Load previous node data for this node
      loadPreviousNode(id);
    } catch (err) {
      console.error('Error fetching node data:', err);
      setError('Failed to load node data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNodeData(nodeId);
    // Load existing images for this story
    fetchExistingImages();
  }, [nodeId, storyId]);

  // Handle clicking outside quickview to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quickviewRef.current && !quickviewRef.current.contains(event.target)) {
        setQuickviewNode(null);
        setQuickviewData(null);
      }
    };

    if (quickviewNode) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [quickviewNode]);

  // Fetch existing images for this story
  const fetchExistingImages = async () => {
    try {
      // Get all nodes for this story
      const { data: nodes, error: nodesError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('image_url')
        .eq('story_id', storyId)
        .not('image_url', 'is', null);

      if (nodesError) throw nodesError;

      // Get the story image as well
      const { data: storyData, error: storyError } = await supabase
        .from('stories_sb2847x9')
        .select('story_image_url')
        .eq('id', storyId)
        .single();

      if (storyError && storyError.code !== 'PGRST116') throw storyError;

      // Combine and filter unique images
      let allImages = nodes
        .map(node => node.image_url)
        .filter(url => url && url.trim() !== '');

      if (storyData && storyData.story_image_url) {
        allImages.push(storyData.story_image_url);
      }

      // Remove duplicates and current image
      const uniqueImages = [...new Set(allImages)]
        .filter(url => url !== imageUrl);

      setExistingImages(uniqueImages);
    } catch (err) {
      console.error('Error fetching existing images:', err);
    }
  };

  // Load the previous node
  const loadPreviousNode = async (currentNodeId) => {
    if (!currentNodeId || currentNodeId === 'new') return;

    try {
      // First get the current node's incoming edges
      const { data: options, error: optionsError } = await supabase
        .from('options_sb2847x9')
        .select('story_node_id')
        .eq('next_node_id', currentNodeId);

      if (optionsError) throw optionsError;

      if (options && options.length > 0) {
        // Get the first previous node (we'll just show one even if there are multiple)
        const { data: prevNode, error: nodeError } = await supabase
          .from('story_nodes_sb2847x9')
          .select('*')
          .eq('id', options[0].story_node_id)
          .single();

        if (!nodeError && prevNode) {
          setPreviousNode(prevNode);
        }
      } else {
        // No previous node found
        setPreviousNode(null);
      }
    } catch (err) {
      console.error('Error loading previous node:', err);
    }
  };

  // Fetch available nodes for this story for option linking
  useEffect(() => {
    const fetchAvailableNodes = async () => {
      try {
        const { data, error } = await supabase
          .from('story_nodes_sb2847x9')
          .select('id,content')
          .eq('story_id', storyId);

        if (error) throw error;
        setAvailableNodes(data || []);
      } catch (err) {
        console.error('Error fetching available nodes:', err);
        setError('Failed to load available nodes for linking.');
      }
    };

    if (storyId) {
      fetchAvailableNodes();
    }
  }, [storyId]);

  const handleImageUpload = async (event) => {
    try {
      setUploading(true);
      const file = event.target.files[0];
      if (!file) return;

      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Generate a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `story-images/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('storybucket')
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('storybucket')
        .getPublicUrl(filePath);

      setImageUrl(publicUrlData.publicUrl);

      setToast({
        show: true,
        message: 'Image uploaded successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectExistingImage = (url) => {
    setImageUrl(url);
    setImagePreview(url);
    setShowExistingImages(false);

    setToast({
      show: true,
      message: 'Image selected',
      type: 'success'
    });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const addOption = () => {
    setOptions([...options, {
      id: `temp-${Date.now()}`,
      option_text: '',
      next_node_id: null
    }]);
  };

  const updateOption = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index][field] = value;
    setOptions(updatedOptions);
  };

  const removeOption = (index) => {
    const updatedOptions = [...options];
    updatedOptions.splice(index, 1);
    setOptions(updatedOptions);
  };

  const moveOptionUp = (index) => {
    if (index === 0) return;
    const updatedOptions = [...options];
    const temp = updatedOptions[index];
    updatedOptions[index] = updatedOptions[index - 1];
    updatedOptions[index - 1] = temp;
    setOptions(updatedOptions);
  };

  const moveOptionDown = (index) => {
    if (index === options.length - 1) return;
    const updatedOptions = [...options];
    const temp = updatedOptions[index];
    updatedOptions[index] = updatedOptions[index + 1];
    updatedOptions[index + 1] = temp;
    setOptions(updatedOptions);
  };

  const handleQuickview = async (nextNodeId) => {
    if (!nextNodeId) return;

    try {
      setLoadingQuickview(true);
      setQuickviewNode(nextNodeId);

      const { data: nodeData, error } = await supabase
        .from('story_nodes_sb2847x9')
        .select('*')
        .eq('id', nextNodeId)
        .single();

      if (error) throw error;
      setQuickviewData(nodeData);
    } catch (err) {
      console.error('Error loading quickview data:', err);
      setToast({
        show: true,
        message: 'Failed to load node preview',
        type: 'error'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 3000);
    } finally {
      setLoadingQuickview(false);
    }
  };

  // Modified to tell parent component to load a different node
  const handleGoToNode = (nextNodeId) => {
    if (!nextNodeId) return;
    
    // Tell the parent component to load this node
    if (onSave) {
      onSave({ action: 'navigate', id: nextNodeId });
    }
  };

  // Modified to tell parent component to load the previous node
  const handleGoToPreviousNode = () => {
    if (!previousNode) return;
    
    // Tell the parent component to load the previous node
    if (onSave) {
      onSave({ action: 'navigate', id: previousNode.id });
    }
  };

  const handleSave = async (returnToMap = false) => {
    if (!content.trim()) {
      setError('Node content is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      // Check if we've already saved this node in this session
      const isNewNode = nodeId === 'new' && !savedNodeId;

      // If this is set as a start node, we need to unset any existing start nodes for this story
      if (isStartNode) {
        await supabase
          .from('story_nodes_sb2847x9')
          .update({ is_start_node: false })
          .eq('story_id', storyId)
          .neq('id', nodeId !== 'new' ? nodeId : (savedNodeId || 'new-id'));

        // Also update the start_node_id in the stories table
        const newNodeId = nodeId !== 'new' ? nodeId : savedNodeId;
        // We'll update this after creating a new node
        await supabase
          .from('stories_sb2847x9')
          .update({ start_node_id: newNodeId })
          .eq('id', storyId);
      }

      // Save or update the node
      let nodeResult;

      if (nodeId !== 'new' || savedNodeId) {
        // Update existing node
        const nodeIdToUse = nodeId !== 'new' ? nodeId : savedNodeId;
        
        const { data, error } = await supabase
          .from('story_nodes_sb2847x9')
          .update({
            content,
            image_url: imageUrl,
            is_start_node: isStartNode,
            is_end_node: isEndNode
          })
          .eq('id', nodeIdToUse)
          .select()
          .single();

        if (error) throw error;
        nodeResult = data;

        // If this was set as a start node, update the stories table
        if (isStartNode) {
          await supabase
            .from('stories_sb2847x9')
            .update({ start_node_id: nodeIdToUse })
            .eq('id', storyId);
        }
        
        // Delete existing options
        await supabase
          .from('options_sb2847x9')
          .delete()
          .eq('story_node_id', nodeIdToUse);
          
      } else {
        // Create new node
        const { data, error } = await supabase
          .from('story_nodes_sb2847x9')
          .insert([{
            story_id: storyId,
            content,
            image_url: imageUrl,
            is_start_node: isStartNode,
            is_end_node: isEndNode
          }])
          .select()
          .single();

        if (error) throw error;
        nodeResult = data;
        
        // Save the ID so we don't create duplicates
        setSavedNodeId(nodeResult.id);

        // If this was set as a start node, update the stories table
        if (isStartNode) {
          await supabase
            .from('stories_sb2847x9')
            .update({ start_node_id: nodeResult.id })
            .eq('id', storyId);
        }
      }

      // Insert all options - allow options with blank next_node_id
      if (options.length > 0) {
        const optionsToInsert = options
          .filter(opt => opt.option_text.trim()) // Only require option_text
          .map(opt => ({
            story_node_id: nodeResult.id,
            option_text: opt.option_text,
            next_node_id: opt.next_node_id || null // Allow null for next_node_id
          }));

        if (optionsToInsert.length > 0) {
          const { error: optionsError } = await supabase
            .from('options_sb2847x9')
            .insert(optionsToInsert);

          if (optionsError) throw optionsError;
        }
      }

      setToast({
        show: true,
        message: 'Node saved successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);

      if (returnToMap) {
        if (onSave) onSave(nodeResult);
      }
    } catch (err) {
      console.error('Error saving node:', err);
      setError('Failed to save node. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStartNode = () => {
    setIsStartNode(!isStartNode);
    // If turning on start node, make sure it's not also an end node
    if (!isStartNode) {
      setIsEndNode(false);
    }
  };

  const toggleEndNode = () => {
    setIsEndNode(!isEndNode);
    // If turning on end node, make sure it's not also a start node
    if (!isEndNode) {
      setIsStartNode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading node...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="node-editor bg-white rounded-3xl shadow-dreamy p-6"
    >
      {/* Header with title and action buttons */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-display font-bold text-purple-700">
          {nodeId && nodeId !== 'new' ? 'Edit Story Node' : 'Create Story Node'}
        </h2>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiSave} className="text-lg" /> Save Node
              </>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiMap} className="text-lg" /> Save & Return
              </>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
            onClick={onCancel}
          >
            <SafeIcon icon={FiIcons.FiX} className="text-lg" /> Cancel
          </motion.button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Previous Node Reference Button (if exists) */}
      {previousNode && (
        <div className="mb-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowPreviousNode(!showPreviousNode)}
            className="flex justify-between items-center w-full cursor-pointer bg-purple-50 px-4 py-3 text-purple-700 font-medium rounded-xl border border-purple-100 hover:bg-purple-100"
          >
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiCornerLeftUp} className="text-purple-500" />
              <span>{showPreviousNode ? "Hide Previous Node" : "Show Previous Node"}</span>
            </div>
            <SafeIcon
              icon={FiIcons.FiChevronDown}
              className={`text-purple-500 transition-transform ${showPreviousNode ? 'rotate-180' : ''}`}
            />
          </motion.button>
          {showPreviousNode && (
            <div className="p-4 mt-2 bg-white border border-purple-100 rounded-xl">
              <div className="bg-gray-50 rounded-xl p-4 text-gray-600 font-fun mb-3">
                {previousNode.content}
              </div>
              <div className="flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-2"
                  onClick={handleGoToPreviousNode}
                  disabled={saving}
                >
                  <SafeIcon icon={FiIcons.FiEdit3} className="text-sm" /> Go to Node
                </motion.button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content Area with inline node type buttons */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <label htmlFor="content" className="text-purple-700 font-medium text-lg">
            Story Content
          </label>

          {/* Inline Node Type Buttons - Less prominent */}
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={toggleStartNode}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                isStartNode
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <SafeIcon
                icon={FiIcons.FiFlag}
                className={`${isStartNode ? 'text-green-600' : 'text-gray-500'} text-xs`}
              />
              {isStartNode ? "Start" : "Set Start"}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={toggleEndNode}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                isEndNode
                  ? 'bg-red-100 text-red-700 border border-red-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-200 hover:border-red-300 hover:bg-red-50'
              }`}
            >
              <SafeIcon
                icon={FiIcons.FiXCircle}
                className={`${isEndNode ? 'text-red-600' : 'text-gray-500'} text-xs`}
              />
              {isEndNode ? "End" : "Set End"}
            </motion.button>
          </div>
        </div>

        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50 min-h-[300px] font-fun"
          placeholder="Write your story content here..."
          disabled={saving}
        />
      </div>

      {/* Story Options/Choices Section */}
      <div className="mb-8 p-4 bg-mint-50 rounded-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-mint-700 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiList} /> Story Options (Player Choices)
          </h3>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-mint-100 hover:bg-mint-200 text-mint-700 px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
            onClick={addOption}
            disabled={saving}
          >
            <SafeIcon icon={FiIcons.FiPlus} className="text-sm" /> Add Option
          </motion.button>
        </div>

        {options.length === 0 ? (
          <div className="bg-white rounded-xl p-4 text-center text-gray-500 border border-mint-100">
            <p>This node has no options yet. Add options to create branching paths for players.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {options.map((option, index) => (
              <div key={option.id} className="bg-white rounded-xl p-4 border border-mint-100">
                <div className="flex items-start gap-3">
                  {/* Reorder Controls */}
                  <div className="flex flex-col space-y-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`p-1 rounded ${
                        index === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      onClick={() => moveOptionUp(index)}
                      disabled={index === 0 || saving}
                      title="Move option up"
                    >
                      <SafeIcon icon={FiIcons.FiChevronUp} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`p-1 rounded ${
                        index === options.length - 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                      onClick={() => moveOptionDown(index)}
                      disabled={index === options.length - 1 || saving}
                      title="Move option down"
                    >
                      <SafeIcon icon={FiIcons.FiChevronDown} />
                    </motion.button>
                  </div>

                  {/* Option Content */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-mint-700 font-medium text-sm mb-1">
                        Option Text
                      </label>
                      <input
                        type="text"
                        value={option.option_text}
                        onChange={(e) => updateOption(index, 'option_text', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-mint-200 focus:border-mint-400 focus:ring focus:ring-mint-300 focus:ring-opacity-50"
                        placeholder="Enter choice text..."
                        disabled={saving}
                      />
                    </div>
                    <div>
                      <label className="block text-mint-700 font-medium text-sm mb-1">
                        Next Node (Optional)
                      </label>
                      <select
                        value={option.next_node_id || ''}
                        onChange={(e) => updateOption(index, 'next_node_id', e.target.value || null)}
                        className="w-full px-3 py-2 rounded-lg border border-mint-200 focus:border-mint-400 focus:ring focus:ring-mint-300 focus:ring-opacity-50"
                        disabled={saving}
                      >
                        <option value="">Select a node (or leave blank)</option>
                        {availableNodes.map((node) => (
                          <option key={node.id} value={node.id}>
                            {node.id === (nodeId !== 'new' ? nodeId : savedNodeId)
                              ? '(This node)'
                              : node.content.substring(0, 30) + (node.content.length > 30 ? '...' : '')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    {/* Quickview Button */}
                    {option.next_node_id && (
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg"
                        onClick={() => handleQuickview(option.next_node_id)}
                        disabled={saving}
                        title="Quick preview of next node"
                      >
                        <SafeIcon icon={FiIcons.FiEye} className="text-sm" />
                      </motion.button>
                    )}

                    {/* Go to Node Button */}
                    {option.next_node_id && (
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg"
                        onClick={() => handleGoToNode(option.next_node_id)}
                        disabled={saving}
                        title="Edit this node"
                      >
                        <SafeIcon icon={FiIcons.FiEdit3} className="text-sm" />
                      </motion.button>
                    )}

                    {/* Delete Button - More minimal */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"
                      onClick={() => removeOption(index)}
                      disabled={saving}
                      title="Delete option"
                    >
                      <SafeIcon icon={FiIcons.FiX} className="text-sm" />
                    </motion.button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scene Image Section */}
      <div className="mb-8 p-4 bg-purple-50 rounded-xl">
        <h3 className="text-lg font-medium text-purple-700 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiImage} /> Scene Image
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Image Controls */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <label className="cursor-pointer bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiUpload} className="text-lg" />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploading || saving}
                />
              </label>
              {existingImages.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1"
                  onClick={() => setShowExistingImages(!showExistingImages)}
                  type="button"
                >
                  <SafeIcon icon={FiIcons.FiImage} className="text-sm" />
                  {showExistingImages ? 'Hide' : 'Select Existing'} Image
                </motion.button>
              )}
              {imageUrl && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                  onClick={() => {
                    setImageUrl('');
                    setImagePreview(null);
                  }}
                  disabled={saving}
                >
                  <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" /> Remove
                </motion.button>
              )}
            </div>

            {uploading && (
              <div className="mt-2 flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500 mr-2"></div>
                <span className="text-purple-600 text-sm">Uploading image...</span>
              </div>
            )}

            {showExistingImages && existingImages.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Select an existing image:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {existingImages.map((url, index) => (
                    <div
                      key={index}
                      className="cursor-pointer relative rounded-lg overflow-hidden border-2 hover:border-primary-400"
                      onClick={() => handleSelectExistingImage(url)}
                    >
                      <img
                        src={url}
                        alt={`Existing image ${index + 1}`}
                        className="w-full h-24 object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity">
                        <SafeIcon icon={FiIcons.FiCheck} className="text-white text-2xl" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image Preview */}
          <div>
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-64 w-full rounded-xl object-cover"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 bg-gray-100 rounded-xl text-gray-400">
                <SafeIcon icon={FiIcons.FiImage} className="text-3xl mb-2" />
                <p>No image selected</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quickview Modal */}
      <AnimatePresence>
        {quickviewNode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div
              ref={quickviewRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-dreamy"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-display font-bold text-purple-700">
                  Node Preview
                </h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-full"
                  onClick={() => {
                    setQuickviewNode(null);
                    setQuickviewData(null);
                  }}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-lg" />
                </motion.button>
              </div>

              {loadingQuickview ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : quickviewData ? (
                <div>
                  {quickviewData.image_url && (
                    <div className="mb-4">
                      <img
                        src={quickviewData.image_url}
                        alt="Node preview"
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-gray-700 font-fun leading-relaxed">
                      {quickviewData.content}
                    </p>
                  </div>
                  {quickviewData.is_start_node && (
                    <div className="mt-2 inline-block bg-green-100 text-green-800 px-2 py-1 rounded-lg text-xs font-medium">
                      Start Node
                    </div>
                  )}
                  {quickviewData.is_end_node && (
                    <div className="mt-2 inline-block bg-red-100 text-red-800 px-2 py-1 rounded-lg text-xs font-medium">
                      End Node
                    </div>
                  )}
                  <div className="mt-4 flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-2"
                      onClick={() => {
                        const targetId = quickviewNode;
                        setQuickviewNode(null);
                        setQuickviewData(null);
                        handleGoToNode(targetId);
                      }}
                    >
                      <SafeIcon icon={FiIcons.FiEdit3} className="text-sm" /> Edit This Node
                    </motion.button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center">Failed to load node data</p>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}
    </motion.div>
  );
};

export default NodeEditor;